<template>
  <div class="sleep-content-grid" :class="{ 'list-view': viewMode === 'list' }">
    <!-- No results state -->
    <div v-if="items.length === 0" class="no-content">
      <div class="no-content-message">
        <i class="icon-search"></i>
        <h3>No content found</h3>
        <p v-if="searchTerm">No results found for "{{ searchTerm }}"</p>
        <p v-else>There isn't any content available yet.</p>
        <div class="no-content-actions">
          <button v-if="searchTerm" class="btn-secondary" @click="$emit('clear-search')">
            Clear search
          </button>
          <button class="btn-primary" @click="$emit('create')">
            Create new content
          </button>
        </div>
      </div>
    </div>

    <!-- Content grid -->
    <div v-else class="content-items">
      <div 
        v-for="item in items" 
        :key="item.id" 
        class="content-item"
        :class="{ 'video-item': item.display_type === 'video' }"
      >
        <!-- Item preview with SleepMediaViewer -->
        <div class="item-preview">
          <SleepMediaViewer :item="item" fit="cover" :muted="true" />
        </div>
        
        <!-- Item details -->
        <div class="item-details">
          <div class="item-header">
            <h3 class="item-title">{{ item.title }}</h3>
            <span class="item-type">{{ getCategoryName(item.type) }}</span>
          </div>
          <div class="item-content" v-html="truncateText(item.details, 100)"></div>
          <div class="item-actions">
            <button class="btn-secondary" @click="$emit('edit', item)">
              <i class="icon-edit"></i> Edit
            </button>
            <button class="btn-danger" @click="$emit('delete', item.id)">
              <i class="icon-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { SleepMediaViewer } from '@/components/media';

export default defineComponent({
  name: 'SleepContentGrid',
  components: {
    SleepMediaViewer
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    searchTerm: {
      type: String,
      default: ''
    },
    viewMode: {
      type: String,
      default: 'grid'
    }
  },
  emits: ['edit', 'delete', 'clear-search', 'create'],
  setup() {
    // Function to get readable category name
    const getCategoryName = (type) => {
      const categoryNames = {
        'bedtime': 'Bedtime Story',
        'breathing': 'Breathing Exercise',
        'sleep_frequency': 'Sleep Frequency',
        'souds_of_nature': 'Sounds of Nature',
        'brain_programming': 'Brain Programming'
      }
      return categoryNames[type] || type
    }
    
    // Function to truncate text for preview
    const truncateText = (text, maxLength) => {
      if (!text) return '';
      
      // Remove HTML tags for accurate length calculation
      const strippedText = text.replace(/<[^>]+>/g, '');
      
      if (strippedText.length <= maxLength) {
        return text;
      }
      
      const truncated = strippedText.substr(0, maxLength);
      return truncated + '...';
    }
    
    return {
      getCategoryName,
      truncateText
    }
  }
});
</script>

<style>
.sleep-content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  padding: 20px 0;
}

.sleep-content-grid.list-view {
  grid-template-columns: 1fr;
}

.content-item {
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.sleep-content-grid.list-view .content-item {
  flex-direction: row;
  align-items: center;
}

.content-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.item-preview {
  height: 180px;
  overflow: hidden;
}

.sleep-content-grid.list-view .item-preview {
  width: 180px;
  min-width: 180px;
  height: 120px;
}

.video-item .item-preview {
  height: 200px;
}

.item-details {
  padding: 15px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.item-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #333;
  flex: 1;
}

.item-type {
  font-size: 0.8rem;
  background-color: #f0f0f0;
  padding: 3px 8px;
  border-radius: 12px;
  color: #555;
  white-space: nowrap;
  margin-left: 8px;
}

.item-content {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 15px;
  flex: 1;
}

.item-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: auto;
}

.no-content {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.no-content-message {
  text-align: center;
  max-width: 400px;
  padding: 20px;
}

.no-content-message i {
  font-size: 3rem;
  color: #ccc;
  margin-bottom: 15px;
}

.no-content-message h3 {
  margin: 0 0 10px;
  color: #555;
}

.no-content-message p {
  margin: 0 0 20px;
  color: #777;
}

.no-content-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .sleep-content-grid {
    grid-template-columns: 1fr;
  }
  
  .sleep-content-grid.list-view .content-item {
    flex-direction: column;
  }
  
  .sleep-content-grid.list-view .item-preview {
    width: 100%;
    height: 180px;
  }
}
</style> 